import { ref } from "vue"
import { FirebaseMessaging } from "@capacitor-firebase/messaging"
import { Capacitor } from "@capacitor/core"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { PushTokenRepository } from "@/repositories/push-token-repository"
import { type PermissionState } from "@capacitor/core"
import { NativeSettings, AndroidSettings, IOSSettings } from "capacitor-native-settings"
/**
 * Declare global window interface for iOS native app
 */
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: Record<string, { postMessage: (msg: unknown) => void }>
    }
  }
}

export function useFirebaseMessaging() {
  const dialogStore = useDialogStore()
  const ptr = new PushTokenRepository()
  const pushPermissionState = ref<PermissionState>("prompt")

  // onMounted(async () => {
  //   if (Capacitor.isNativePlatform()) {
  //     await initializeNativePush()
  //   }
  // })

  async function initializeNativePush() {
    await requestPermissions()
    await addListeners()
  }

  async function requestPermissions() {
    let permStatus = await FirebaseMessaging.checkPermissions()
    pushPermissionState.value = permStatus.receive

    if (permStatus.receive === "prompt") {
      permStatus = await FirebaseMessaging.requestPermissions()
      pushPermissionState.value = permStatus.receive
    }

    if (permStatus.receive === "denied") {
      dialogStore.currentDialog = {
        type: DialogTypes.CONFIRM,
        title: "Meldingen staan uit",
        body: "Om meldingen te ontvangen bij wijzigingen in de planning, moet je meldingen aanzetten in je apparaat instellingen.",
        textConfirm: "Open instellingen",
        onConfirm: () => {
          NativeSettings.open({
            optionAndroid: AndroidSettings.ApplicationDetails,
            optionIOS: IOSSettings.App
          })

          dialogStore.currentDialog = null
        }
      }
    }

    if (permStatus.receive === "granted") {
      await registerPush()
    }
  }

  /**
   * Checks if push notification permission has been granted without requesting it
   * @returns boolean indicating if permission is granted
   */
  async function hasPushPermission(): Promise<boolean> {
    const permStatus = await FirebaseMessaging.checkPermissions()
    pushPermissionState.value = permStatus.receive
    return permStatus.receive === "granted"
  }

  async function registerPush() {
    const token = await FirebaseMessaging.getToken()
    console.log("FCM registration success:", token.token)

    const isIOS = Capacitor.getPlatform() === "ios"
    const isAndroid = Capacitor.getPlatform() === "android"

    await ptr.post({
      type: isIOS ? "ios" : isAndroid ? "android" : "web",
      token: token.token
    })
  }

  async function addListeners() {
    // Token refresh
    await FirebaseMessaging.addListener("tokenReceived", async (token) => {
      console.log("FCM token refreshed:", token.token)

      const isIOS = Capacitor.getPlatform() === "ios"
      const isAndroid = Capacitor.getPlatform() === "android"

      await ptr.post({
        type: isIOS ? "ios" : isAndroid ? "android" : "web",
        token: token.token
      })
    })

    // Message received in foreground
    await FirebaseMessaging.addListener("notificationReceived", (notification) => {
      console.log("Push notification received:", notification)
      dialogStore.currentDialog = {
        type: DialogTypes.CONFIRM,
        title: notification.notification?.title || "Instructie wijzigingen",
        body:
          notification.notification?.body ||
          "Er zijn wijzigingen aangebracht in de instructie. Bevestig dat je op de hoogte bent van de wijzigingen.",
        textConfirm: "Ga naar instructies",
        onConfirm: () => {
          dialogStore.currentDialog = null
        }
      }
    })

    // Message opened from background
    await FirebaseMessaging.addListener("notificationActionPerformed", (notification) => {
      console.log("Push notification opened:", notification)
      dialogStore.currentDialog = {
        type: DialogTypes.CONFIRM,
        title: notification.notification?.title || "Instructie wijzigingen",
        body:
          notification.notification?.body ||
          "Er zijn wijzigingen aangebracht in de instructie. Bevestig dat je op de hoogte bent van de wijzigingen.",
        textConfirm: "Ga naar instructies",
        onConfirm: () => {
          dialogStore.currentDialog = null
        }
      }
    })
  }

  async function getDeliveredNotifications() {
    console.warn("getDeliveredNotifications is not supported with Firebase Messaging")
    return []
  }

  return {
    pushPermissionState,
    initializeNativePush,
    getDeliveredNotifications,
    hasPushPermission
  }
}
